import * as React from 'react';
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

const initialForm = {
  topic_filter: '',
  scope: 'global',
  direction: 'bidirectional',
  description: '',
};

export default function AdminMqttTopicsPage(props) {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [form, setForm] = useState(initialForm);

  async function loadTopics() {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/mqtt/topics');
      setTopics(Array.isArray(response.data?.topics) ? response.data.topics : []);
    } catch (loadError) {
      setError(loadError?.response?.data?.message || 'Unable to load MQTT topics.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTopics();
  }, []);

  async function handleCreate(event) {
    event.preventDefault();
    setFeedback('');
    setError('');
    try {
      await axiosInstance.post('/mqtt/topics', form);
      setForm(initialForm);
      setFeedback('MQTT topic created.');
      await loadTopics();
    } catch (createError) {
      setError(createError?.response?.data?.message || 'Unable to create MQTT topic.');
    }
  }

  async function toggleTopic(topic) {
    try {
      await axiosInstance.patch(`/mqtt/topics/${topic.topic_id}`, { is_active: !topic.is_active });
      await loadTopics();
    } catch (patchError) {
      setError(patchError?.response?.data?.message || 'Unable to update MQTT topic.');
    }
  }

  async function deleteTopic(topicId) {
    try {
      await axiosInstance.delete(`/mqtt/topics/${topicId}`);
      await loadTopics();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || 'Unable to delete MQTT topic.');
    }
  }

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Admin Dashboard', href: '/admin' }, { label: 'MQTT Topics Registry' }]}
      title="MQTT Topics Registry"
      subtitle="Global AMX topic catalog and the lifecycle controls for topic filters."
    >
      {feedback ? <Alert severity="success">{feedback}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <AdminSection title="Create Topic" subtitle="Register a new MQTT topic filter for partner and gateway assignment.">
        <Stack component="form" spacing={2} onSubmit={handleCreate}>
          <TextField label="Topic Filter" value={form.topic_filter} onChange={(event) => setForm({ ...form, topic_filter: event.target.value })} fullWidth required />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField select label="Scope" value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value })} fullWidth>
              <MenuItem value="local">local</MenuItem>
              <MenuItem value="p2p">p2p</MenuItem>
              <MenuItem value="global">global</MenuItem>
            </TextField>
            <TextField select label="Direction" value={form.direction} onChange={(event) => setForm({ ...form, direction: event.target.value })} fullWidth>
              <MenuItem value="publish">publish</MenuItem>
              <MenuItem value="subscribe">subscribe</MenuItem>
              <MenuItem value="bidirectional">bidirectional</MenuItem>
            </TextField>
          </Stack>
          <TextField label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} fullWidth multiline minRows={2} />
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained">Create Topic</Button>
          </Stack>
        </Stack>
      </AdminSection>

      <AdminSection title="Topic Catalog" subtitle="Current MQTT topic definitions available for policy assignment.">
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <span>Loading topics...</span>
          </Stack>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Topic Filter</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topics.map((topic) => (
                <TableRow key={topic.topic_id} hover>
                  <TableCell>{topic.topic_filter}</TableCell>
                  <TableCell>{topic.scope}</TableCell>
                  <TableCell>{topic.direction}</TableCell>
                  <TableCell>{topic.is_active ? 'active' : 'inactive'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => toggleTopic(topic)}>
                        {topic.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => deleteTopic(topic.topic_id)}>
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminSection>
    </AdminLayout>
  );
}
